import React, { useState, useEffect } from 'react';
import {
  Handshake,
  Building2,
  TrendingUp,
  Users,
  Euro,
  Calculator,
  Shield,
  Award,
  CheckCircle,
  ArrowRight,
  Play,
  Star,
  MapPin,
  Clock,
  Zap,
  Target,
  Heart,
  Home,
  Gem,
  Brain,
  Eye,
  Phone,
  Mail,
  MessageCircle
} from 'lucide-react';

const SharedEconomyLanding = () => {
  const [investmentAmount, setInvestmentAmount] = useState(50000);
  const [selectedProperty, setSelectedProperty] = useState(0);
  const [isCalculating, setIsCalculating] = useState(false);

  // Mock data for properties
  const properties = [
    {
      id: 1,
      title: "Villa Sonnenschein",
      location: "Mallorca, Spanien",
      image: "/api/placeholder/600/400",
      price: 850000,
      availableShares: 25,
      monthlyReturn: 680,
      yearlyReturn: 8160,
      features: ["Meerblick", "Pool", "5 Schlafzimmer", "Garten"]
    },
    {
      id: 2,
      title: "Chalet Alpenpanorama",
      location: "Tirol, Österreich",
      image: "/api/placeholder/600/400",
      price: 720000,
      availableShares: 12.5,
      monthlyReturn: 580,
      yearlyReturn: 6960,
      features: ["Bergblick", "Sauna", "4 Schlafzimmer", "Terrasse"]
    },
    {
      id: 3,
      title: "Penthouse Stadtblick",
      location: "München, Deutschland",
      image: "/api/placeholder/600/400",
      price: 1200000,
      availableShares: 8.3,
      monthlyReturn: 920,
      yearlyReturn: 11040,
      features: ["Stadtblick", "Dachterrasse", "3 Schlafzimmer", "Modern"]
    }
  ];

  const calculateROI = () => {
    setIsCalculating(true);
    setTimeout(() => {
      setIsCalculating(false);
    }, 1000);
  };

  useEffect(() => {
    calculateROI();
  }, [investmentAmount, selectedProperty]);

  const currentProperty = properties[selectedProperty];
  const sharePercentage = (investmentAmount / currentProperty.price) * 100;
  const monthlyReturn = (sharePercentage / 100) * currentProperty.monthlyReturn;
  const yearlyReturn = monthlyReturn * 12;

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Video Placeholder */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-purple-900/80">
          <div className="absolute inset-0 bg-black/20"></div>
          <img 
            src="/api/placeholder/1920/1080" 
            alt="Luxury Property" 
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* Hero Content */}
        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-6">
          <div className="mb-6">
            <span className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 text-sm font-medium">
              <Zap className="w-4 h-4 mr-2 text-yellow-400" />
              Powered by Clara360 - Deutschlands #1 PropTech
            </span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Dein Traumhaus<br />
            <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
              wartet bereits
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl mb-8 text-gray-200 max-w-2xl mx-auto">
            Investiere in Premium-Immobilien mit vollständiger Transparenz. 
            Während andere träumen, baust du bereits Vermögen auf.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all transform hover:scale-105 flex items-center">
              <Calculator className="w-5 h-5 mr-2" />
              Jetzt Rendite berechnen
            </button>
            <button className="border-2 border-white/30 hover:border-white/50 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all backdrop-blur-sm flex items-center">
              <Play className="w-5 h-5 mr-2" />
              Wie es funktioniert
            </button>
          </div>
          
          {/* Trust Indicators */}
          <div className="mt-12 flex flex-wrap justify-center items-center gap-8 text-sm text-gray-300">
            <div className="flex items-center">
              <Shield className="w-5 h-5 mr-2 text-green-400" />
              BaFin reguliert
            </div>
            <div className="flex items-center">
              <Users className="w-5 h-5 mr-2 text-blue-400" />
              1.200+ Investoren
            </div>
            <div className="flex items-center">
              <Award className="w-5 h-5 mr-2 text-yellow-400" />
              8,2% Ø Rendite
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/50 rounded-full mt-2"></div>
          </div>
        </div>
      </section>

      {/* Interactive ROI Calculator */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Transparenz, die MYNE nicht bietet
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Berechne deine exakte Rendite mit echten Daten aus unserem Portfolio. 
              Keine versteckten Kosten, keine Überraschungen.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Calculator */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Investment-Rechner
              </h3>
              
              {/* Investment Amount Slider */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Investment-Betrag
                </label>
                <div className="relative">
                  <input
                    type="range"
                    min="25000"
                    max="500000"
                    step="5000"
                    value={investmentAmount}
                    onChange={(e) => setInvestmentAmount(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-sm text-gray-500 mt-2">
                    <span>25.000 €</span>
                    <span className="font-bold text-blue-600">
                      {investmentAmount.toLocaleString('de-DE')} €
                    </span>
                    <span>500.000 €</span>
                  </div>
                </div>
              </div>
              
              {/* Property Selection */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Immobilie auswählen
                </label>
                <div className="grid grid-cols-1 gap-3">
                  {properties.map((property, index) => (
                    <button
                      key={property.id}
                      onClick={() => setSelectedProperty(index)}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        selectedProperty === index
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="font-semibold">{property.title}</h4>
                          <p className="text-sm text-gray-600">{property.location}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-600">
                            {property.availableShares}% verfügbar
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Results */}
              <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 mb-4">Deine Rendite</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Anteil an Immobilie</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {sharePercentage.toFixed(1)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Monatliche Rendite</p>
                    <p className="text-2xl font-bold text-green-600">
                      {isCalculating ? '...' : `${monthlyReturn.toFixed(0)} €`}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Jährliche Rendite</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {isCalculating ? '...' : `${yearlyReturn.toFixed(0)} €`}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">ROI p.a.</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {((yearlyReturn / investmentAmount) * 100).toFixed(1)}%
                    </p>
                  </div>
                </div>
              </div>
              
              <button className="w-full mt-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all">
                Investment starten
              </button>
            </div>
            
            {/* Property Showcase */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="relative h-64">
                  <img 
                    src={currentProperty.image} 
                    alt={currentProperty.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {currentProperty.availableShares}% verfügbar
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {currentProperty.title}
                  </h3>
                  <p className="text-gray-600 mb-4 flex items-center">
                    <MapPin className="w-4 h-4 mr-1" />
                    {currentProperty.location}
                  </p>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    {currentProperty.features.map((feature, index) => (
                      <div key={index} className="flex items-center text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                        {feature}
                      </div>
                    ))}
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Immobilienwert</span>
                      <span className="font-bold text-gray-900">
                        {currentProperty.price.toLocaleString('de-DE')} €
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Warum Clara360 MYNE überlegen ist
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Während MYNE verspricht, liefern wir bereits seit Jahren Ergebnisse.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Eye className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                100% Transparenz
              </h3>
              <p className="text-gray-600">
                Alle Kosten, Renditen und Risiken offen dargelegt. 
                Keine versteckten Gebühren wie bei MYNE.
              </p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Sofortige Liquidität
              </h3>
              <p className="text-gray-600">
                Verkaufe deine Anteile jederzeit über unseren Marktplatz. 
                Keine Wartezeiten wie bei der Konkurrenz.
              </p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Brain className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                KI-gestützte Auswahl
              </h3>
              <p className="text-gray-600">
                Unsere KI analysiert Marktdaten und wählt nur die besten 
                Investmentmöglichkeiten für dich aus.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-purple-900 text-white">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="text-4xl font-bold mb-4">
            Bereit für dein erstes Investment?
          </h2>
          <p className="text-xl mb-8 text-gray-200">
            Schließe dich 1.200+ erfolgreichen Investoren an und starte noch heute.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button className="bg-white text-blue-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all flex items-center">
              <Phone className="w-5 h-5 mr-2" />
              Kostenlose Beratung
            </button>
            <button className="border-2 border-white/30 hover:border-white/50 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all flex items-center">
              <MessageCircle className="w-5 h-5 mr-2" />
              WhatsApp Chat
            </button>
          </div>
          
          <p className="text-sm text-gray-300 mt-6">
            Unverbindlich und kostenfrei. Keine Verpflichtungen.
          </p>
        </div>
      </section>
    </div>
  );
};

export default SharedEconomyLanding;
